import { supabase } from './supabase';

function parseStringArray(raw: unknown): string[] {
  if (Array.isArray(raw) && raw.every((x) => typeof x === 'string')) return raw;
  if (typeof raw === 'string') {
    try {
      const v = JSON.parse(raw || '[]');
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** @deprecated Use buildUnifiedContext() from heliosEngine.ts instead. Kept for UserContextProvider compatibility. */
export async function buildUserContext(userId: string) {
  const todayDate = new Date().toISOString().split('T')[0];
  const [
    profileRes,
    analysesRes,
    goalsRes,
    logsRes,
    skillsRes,
    todayRecoveryRes,
  ] = await Promise.all([
    supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
    supabase
      .from('analyses')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(5),
    supabase.from('goals').select('*').eq('user_id', userId).maybeSingle(),
    supabase
      .from('workout_logs')
      .select('*')
      .eq('user_id', userId)
      .order('logged_at', { ascending: false })
      .limit(10),
    supabase.from('user_skills').select('*').eq('user_id', userId),
    supabase
      .from('recovery_checkins')
      .select('recovery_score, sleep_hours, energy_level, soreness_level, hydration, nutrition_quality')
      .eq('user_id', userId)
      .eq('checkin_date', todayDate)
      .maybeSingle(),
  ]);

  const profile = profileRes.data;
  const analyses = (analysesRes.data as Record<string, unknown>[]) || [];
  const latest = analyses[0] as Record<string, unknown> | undefined;
  const goals = goalsRes.data as Record<string, unknown> | null;
  const logs = (logsRes.data as { feeling?: string | null }[]) || [];
  const skills = (skillsRes.data as { status?: string; skill_id?: string }[]) || [];
  const todayRecovery = todayRecoveryRes.data as {
    recovery_score: number | null;
    sleep_hours: number | null;
    energy_level: number | null;
    soreness_level: number | null;
    hydration: string | null;
    nutrition_quality: string | null;
  } | null;

  const avgFeeling =
    logs.length > 0
      ? logs.reduce((acc, l) => {
          const n = { great: 4, good: 3, okay: 2, tough: 1 }[l.feeling || ''] ?? 2;
          return acc + n;
        }, 0) / logs.length
      : 3;

  const recentFeelings = logs.slice(0, 5).map((l) => l.feeling).filter(Boolean) as string[];
  const isOvertraining = recentFeelings.filter((f) => f === 'tough').length >= 3;
  const isUnderchallenged = recentFeelings.filter((f) => f === 'great').length >= 4;

  const skillsInProgress = skills.filter((s) => s.status === 'in_progress').map((s) => s.skill_id!);
  const skillsMastered = skills.filter((s) => s.status === 'unlocked').map((s) => s.skill_id!);

  const progressTrend =
    analyses.length >= 2
      ? Number(analyses[0]?.overall_score) - Number(analyses[analyses.length - 1]?.overall_score)
      : 0;

  const weakPoints = latest ? parseStringArray(latest.weak_points) : [];
  const strengths = latest ? parseStringArray(latest.strengths) : [];
  const trainingPriorities = latest ? parseStringArray(latest.training_priorities) : [];

  const heightCm = profile?.height_cm != null ? Number(profile.height_cm) : null;
  const weightKg = profile?.weight_kg != null ? Number(profile.weight_kg) : null;
  const bmi =
    heightCm && weightKg && heightCm > 0
      ? (weightKg / (heightCm / 100) ** 2).toFixed(1)
      : null;

  const latestBodyFat = latest?.body_fat != null ? Number(latest.body_fat) : null;
  const bodyFatStr =
    latestBodyFat != null ? String(latestBodyFat) : undefined;

  const feedback = (profile?.workout_feedback as Record<string, unknown> | undefined) ?? {} as Record<string, unknown>;
  const fbLiked = Array.isArray(feedback.liked) ? feedback.liked as string[] : [];
  const fbDisliked = Array.isArray(feedback.disliked) ? feedback.disliked as string[] : [];
  const fbStrong = Array.isArray(feedback.strong) ? feedback.strong as string[] : [];
  const fbStruggling = Array.isArray(feedback.struggling) ? feedback.struggling as string[] : [];
  let workoutFeedbackContext = '';
  if (fbLiked.length) {
    workoutFeedbackContext += `\nExercises the user enjoys: ${fbLiked.join(', ')}.`;
  }
  if (fbDisliked.length) {
    workoutFeedbackContext += `\nExercises the user dislikes or struggles with: ${fbDisliked.join(', ')} — suggest alternatives or accessory work to address weaknesses.`;
  }
  if (fbStrong.length) {
    workoutFeedbackContext += `\nExercises where they are gaining strength: ${fbStrong.join(', ')} — progressive overload here.`;
  }
  if (fbStruggling.length) {
    workoutFeedbackContext += `\nExercises they are struggling with: ${fbStruggling.join(', ')} — add supporting exercises, reduce volume, focus on form.`;
  }
  if (feedback.last_feeling) {
    workoutFeedbackContext += `\nTheir last session feeling: ${feedback.last_feeling}.`;
  }
  if (feedback.avg_completion) {
    const pct = Math.round(Number(feedback.avg_completion) * 100);
    if (pct >= 90) workoutFeedbackContext += `\nAverage session completion: ${pct}% — they are consistent, can increase volume.`;
    else if (pct >= 70)
      workoutFeedbackContext += `\nAverage session completion: ${pct}% — maintain current volume.`;
    else workoutFeedbackContext += `\nAverage session completion: ${pct}% — reduce volume, workouts may be too long or intense.`;
  }

  return {
    profile,

    age: profile?.age != null ? Number(profile.age) : undefined,
    gender: profile?.gender as string | undefined,
    heightCm,
    weightKg,
    bmi,

    goal: profile?.goal as string | undefined,
    experience: profile?.experience as string | undefined,
    equipment: profile?.equipment as string | undefined,
    trainingStyle: (profile?.workout_style as string | undefined) ?? undefined,
    trainingDays: profile?.training_days_per_week != null
      ? Number(profile.training_days_per_week)
      : undefined,

    latestScore: latest != null ? Number(latest.overall_score) : undefined,
    bodyFat: bodyFatStr,
    muscleSymmetry: latest != null ? Number(latest.muscle_symmetry) : undefined,
    weakPoints,
    strengths,
    trainingPriorities,

    targetScore: goals?.target_overall_score != null ? Number(goals.target_overall_score) : undefined,
    targetBodyFat: goals?.target_body_fat != null ? Number(goals.target_body_fat) : undefined,
    targetSymmetry:
      goals?.target_muscle_symmetry != null ? Number(goals.target_muscle_symmetry) : undefined,
    targetWeight: goals?.target_weight != null ? Number(goals.target_weight) : undefined,
    pointsToGoal:
      goals?.target_overall_score != null && latest != null
        ? Number(goals.target_overall_score) - Number(latest.overall_score)
        : null,

    avgSessionFeeling: avgFeeling,
    recentFeelings,
    isOvertraining,
    isUnderchallenged,
    totalSessionsLogged: logs.length,

    skillsInProgress,
    skillsMastered,

    progressTrend,
    totalAnalyses: analyses.length,
    analysisDates: analyses.map((a) => String(a.created_at ?? '')),

    shouldReduceVolume: isOvertraining,
    shouldIncreaseIntensity: isUnderchallenged,
    needsMoreProtein:
      profile?.goal === 'Build Muscle' ||
      (latestBodyFat != null && latestBodyFat > 15),
    isInDeficit: profile?.goal === 'Lose Fat',
    isBulking: profile?.goal === 'Build Muscle',
    isRecomping: profile?.goal === 'Both',
    workoutFeedback: feedback,
    workoutFeedbackContext,
    todayRecoveryScore: todayRecovery?.recovery_score ?? null,
    todayRecovery,
  };
}

export type UserContext = Awaited<ReturnType<typeof buildUserContext>>;

// ─── Coach memory context (premium only) ────────────────────────────────────

export async function buildCoachMemoryContext(userId: string): Promise<string> {
  const today = new Date().toISOString().split('T')[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const [sessionsRes, analysesRes, recoveryRes, skillsRes, profileRes] = await Promise.all([
    supabase
      .from('workout_logs')
      .select('logged_at, feeling, exercises_completed, session_duration_min')
      .eq('user_id', userId)
      .order('logged_at', { ascending: false })
      .limit(5),
    supabase
      .from('analyses')
      .select('overall_score, weak_points, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(3),
    supabase
      .from('recovery_checkins')
      .select('checkin_date, recovery_score, sleep_hours, energy_level, soreness_level')
      .eq('user_id', userId)
      .gte('checkin_date', sevenDaysAgo.toISOString().split('T')[0])
      .order('checkin_date', { ascending: false })
      .limit(7),
    supabase
      .from('user_skills')
      .select('skill_id, status')
      .eq('user_id', userId)
      .in('status', ['in_progress', 'unlocked']),
    supabase
      .from('profiles')
      .select('injuries, sports, training_philosophy, goal, pending_coach_observation')
      .eq('id', userId)
      .maybeSingle(),
  ]);

  const sessions = (sessionsRes.data as Record<string, unknown>[] | null) ?? [];
  const analyses = (analysesRes.data as { overall_score: number; weak_points: unknown; created_at: string }[] | null) ?? [];
  const recovery = (recoveryRes.data as { checkin_date: string; recovery_score: number | null }[] | null) ?? [];
  const skills = (skillsRes.data as { skill_id: string; status: string }[] | null) ?? [];
  const profile = profileRes.data as { injuries?: string | null; sports?: string | null; training_philosophy?: string | null; goal?: string | null; pending_coach_observation?: string | null } | null;

  // Last session
  const lastSession = sessions[0];
  const lastSessionLine = lastSession
    ? `${String(lastSession.logged_at ?? '').split('T')[0]} — feeling: ${lastSession.feeling ?? 'not noted'}${lastSession.session_duration_min ? `, ${lastSession.session_duration_min}min` : ''}`
    : 'no recent sessions';

  // Analysis delta
  const latestScore = analyses[0]?.overall_score;
  const prevScore = analyses[1]?.overall_score;
  const delta = latestScore != null && prevScore != null ? latestScore - prevScore : null;
  const parseWP = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
    if (typeof v === 'string') { try { const j = JSON.parse(v); return Array.isArray(j) ? j : []; } catch { return []; } }
    return [];
  };
  const weakPoints = parseWP(analyses[0]?.weak_points).slice(0, 2).join(', ') || 'none';
  const analysisLine = latestScore != null
    ? `score ${latestScore}${delta != null ? ` (${delta > 0 ? '+' : ''}${delta} vs last)` : ''} — weak: ${weakPoints}`
    : 'no analyses yet';

  // Recovery
  const recovScores = recovery.map((r) => r.recovery_score).filter((s): s is number => s != null);
  const recovAvg = recovScores.length > 0 ? Math.round(recovScores.reduce((a, b) => a + b, 0) / recovScores.length) : null;
  const recovTrend = recovScores.length >= 3
    ? (recovScores[0] > recovScores[recovScores.length - 1] ? 'improving' : recovScores[0] < recovScores[recovScores.length - 1] ? 'declining' : 'stable')
    : 'not enough data';
  const recoveryLine = recovAvg != null ? `avg ${recovAvg}/100 last ${recovScores.length} days, ${recovTrend}` : 'no check-in data';

  // Active skill
  const activeSkill = skills.find((s) => s.status === 'in_progress')?.skill_id ?? null;

  // Injuries
  const injuryLine = profile?.injuries ? String(profile.injuries) : 'none';

  const todayDate = today;
  void todayDate; // used in query above

  return `USER CONTEXT (from your coach memory):
- Last session: ${lastSessionLine}
- Analysis: ${analysisLine}
- Recovery: ${recoveryLine}
- Active injury: ${injuryLine}
- Working toward: ${activeSkill ?? 'none'}
- Goals: ${profile?.goal ?? 'not set'}${profile?.training_philosophy ? ` | Philosophy: ${profile.training_philosophy}` : ''}${profile?.sports ? ` | Sport: ${profile.sports}` : ''}`;
}

export function buildSystemPrompt(
  ctx: UserContext,
  opts?: { memoryContext?: string; recentAdvice?: string[]; isPremium?: boolean }
): string {
  const { memoryContext, recentAdvice = [], isPremium = false } = opts ?? {};

  let unlockedSkills: string[] = [];
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem('helios-skilltree-v2');
      unlockedSkills = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(unlockedSkills)) unlockedSkills = [];
    } catch {
      unlockedSkills = [];
    }
  }
  const unlockedSkillsText =
    unlockedSkills.length > 0 ? unlockedSkills.join(', ') : 'none yet';

  const adviceBlock = recentAdvice.length > 0
    ? `\nADVICE ALREADY GIVEN (do not repeat these — build forward, reference as established):\n${recentAdvice.map((a, i) => `${i + 1}. ${a}`).join('\n')}`
    : '';

  const memBlock = isPremium && memoryContext ? `${memoryContext}\n\n` : '';

  return `${memBlock}You are Chiron — the AI coach inside HELIOS. Named after the centaur who trained Achilles, Heracles, and Jason.

COACH CHARACTER:
Direct. Warm when earned. Never clinical. Never generic. Specific always.
You remember everything: reference past sessions, scores, specific exercises by name.
You are honest: tell users what they need to hear, not what they want to hear.
You are brief: under 5 sentences unless detail is genuinely needed.
You are a coach, not a therapist: no emotional processing language, no "how does that make you feel."
Every response ends with a concrete next step or observation — never just validation.

STRICT RULES:
- Never say "great job" or "well done" generically. If acknowledging effort, be specific: "225lb bench at 3 reps — that's a meaningful step."
- Never use therapy-adjacent language.
- Always use the user's name if known.
- Maximum one mention of their physique score per conversation unless they bring it up.
- When user pushes back with specific reasoning: acknowledge it explicitly ("That's fair — if your schedule makes Tuesday better, adjust it. The frequency matters more than the exact day.") Never dig in.
${adviceBlock}

EFFORT RECOGNITION RULE: When user data shows 4+ training sessions last week or they've consistently hit or exceeded prescribed reps, open your response with a specific acknowledgment before anything else. Name the specific exercises or muscle groups. One sentence maximum. Never generic ("great job"). Always specific ("Four sessions. The upper body volume is stacking up.").

PR AND MILESTONE ACKNOWLEDGMENT:
When context shows a new PR or milestone: acknowledge specifically in one sentence ("225lb bench for 3 reps — that's been your target for two months."), then move forward. Never use generic phrases.

RECOVERY-AWARE TONE:
- Score below 40: quieter, measured tone. Lean toward rest and repair framing. Avoid high-energy language.
- Score above 70 AND recent PR: slightly more energized — still specific, never hype.

PHYSIQUE SCORE (reference contextually, not obsessively):
When relevant, reference their score and top weak points: "Given your upper chest is your current priority, I'd lean into more incline pressing this week." Never open a conversation with the score unless they just ran an analysis.

STRUCTURED OUTPUT RULES (append to message when applicable):
When you have 2-3 specific follow-up questions worth asking, append exactly:
[CHIPS: "first chip" | "second chip" | "third chip"]

When you propose a specific action the user should confirm (save workout, add skill, update goal):
[ACTION: {"type":"save_workout|add_skill|update_goal","description":"short label","data":{}}]

When you generate a complete workout session (respond to requests like "build me a session for today"):
[SESSION: {"dayLabel":"Today","focusLabel":"[muscle group]","exercises":[{"name":"...","sets":3,"reps":"8-10","notes":"..."}]}]

These tags are invisible to the user — they trigger UI actions. Strip them from your visible response.

RESPONSE FORMAT:
- 2-4 sentences for simple questions.
- Plans/lists: simple numbered format, max 5 items, one sentence each.
- No headers, no bold, no markdown.
- End with a question OR an action, never a trailing statement.

${isPremium ? '' : 'FREE TIER: No conversation memory. Treat each session fresh.'}



CLIENT PROFILE:
- Age: ${ctx.age ?? 'unknown'} | Gender: ${ctx.gender ?? 'unknown'}
- Height: ${ctx.heightCm != null ? ctx.heightCm + 'cm' : 'unknown'} | Weight: ${ctx.weightKg != null ? ctx.weightKg + 'kg' : 'unknown'} | BMI: ${ctx.bmi ?? 'unknown'}
- Goal: ${ctx.goal ?? 'unknown'} | Experience: ${ctx.experience ?? 'unknown'}
- Training style: ${ctx.trainingStyle ?? 'unknown'} | Training days/week: ${ctx.trainingDays ?? 'unknown'}
- Equipment: ${ctx.equipment ?? 'unknown'}

LATEST PHYSIQUE DATA:
- Overall score: ${ctx.latestScore ?? 'no analysis yet'}/100
- Body fat: ${ctx.bodyFat ?? 'unknown'}%
- Muscle symmetry: ${ctx.muscleSymmetry ?? 'unknown'}/100
- Weak points: ${ctx.weakPoints.join(', ') || 'none recorded'}
- Strengths: ${ctx.strengths.join(', ') || 'none recorded'}
- Training priorities from AI: ${ctx.trainingPriorities.join(', ') || 'none'}

GOALS:
- Target score: ${ctx.targetScore ?? 'not set'} (${ctx.pointsToGoal != null ? ctx.pointsToGoal + ' points away' : 'n/a'})
- Target body fat: ${ctx.targetBodyFat ?? 'not set'}%
- Target weight: ${ctx.targetWeight ?? 'not set'}kg

TODAY'S RECOVERY:
- Recovery score: ${ctx.todayRecoveryScore != null ? `${ctx.todayRecoveryScore}/100 (${ctx.todayRecoveryScore < 40 ? 'rest day' : ctx.todayRecoveryScore < 60 ? 'light activity' : ctx.todayRecoveryScore < 80 ? 'ready to train' : 'optimal'})` : 'no check-in today'}

TRAINING HISTORY:
- Sessions logged: ${ctx.totalSessionsLogged}
- Recent session feelings: ${ctx.recentFeelings.join(', ') || 'none'}
- Average feeling: ${ctx.avgSessionFeeling.toFixed(1)}/4
- Overtraining risk: ${ctx.isOvertraining ? 'YES — reduce volume' : 'no'}
- Underchallenged: ${ctx.isUnderchallenged ? 'YES — increase intensity' : 'no'}
${ctx.workoutFeedbackContext || ''}

SKILLS:
- In progress: ${ctx.skillsInProgress.join(', ') || 'none'}
- Mastered: ${ctx.skillsMastered.join(', ') || 'none'}

SKILL PROGRESS:
Unlocked skills: ${unlockedSkillsText}
Reference skill progress naturally when relevant. If the user asks about pull training and they have pull-ups unlocked, acknowledge it and point toward the next skill in the chain. If they are working toward a specific skill, tailor advice to accelerate that progression.

PROGRESS:
- Total analyses: ${ctx.totalAnalyses}
- Score trend: ${ctx.progressTrend > 0 ? '+' + ctx.progressTrend + ' improving' : ctx.progressTrend < 0 ? ctx.progressTrend + ' declining' : 'stable'}

COACHING INSTRUCTIONS:
- Always reference their specific data above — never give generic advice
- If overtraining: prioritize recovery, suggest deload
- If underchallenged: push harder, suggest progressive overload
- Tailor nutrition to their goal: ${ctx.isBulking ? 'caloric surplus, high protein' : ctx.isInDeficit ? 'caloric deficit, preserve muscle' : 'maintenance, body recomp'}${ctx.todayRecoveryScore != null && ctx.todayRecoveryScore < 50 ? `\n- RECOVERY NUTRITION TODAY (score ${ctx.todayRecoveryScore}/100 — low): shift meal suggestions toward anti-inflammatory foods (salmon, berries, leafy greens, turmeric), emphasize higher protein for muscle repair, include electrolytes in at least one meal slot. Label the plan "Recovery nutrition — adapted for today". Add a brief coach note: "Low recovery today — I've shifted your nutrition to support repair."` : ''}
- Prioritize fixing their weak points: ${ctx.weakPoints[0] ?? 'focus on overall development'}
- Account for their training style: ${ctx.trainingStyle ?? 'unknown'} specific exercises only
- Match recommendations to their equipment: ${ctx.equipment ?? 'unknown'}
- Consider their experience level: ${ctx.experience ?? 'unknown'} appropriate progressions
PROGRESSIVE OVERLOAD RULES — apply these every time you generate a workout plan:
- If avg_completion >= 90% and last_feeling is "great": increase sets by 1 or weight recommendation by 5-10% on main lifts
- If avg_completion < 70% or last_feeling is "drained": reduce total sets by 10-15%, keep intensity
- For exercises in "strong" list: add a top set or increase rep range by 2
- For exercises in "struggling" list: add 1-2 accessory exercises targeting same muscle group, reduce main lift volume
- For exercises in "disliked" list: replace with alternative that hits same muscle group
- For exercises in "liked" list: keep in plan, prioritize these
- Never completely remove a muscle group — always maintain balance
- Progressive overload should feel natural — never jump more than 10% in a single week

PROGRESSIVE OVERLOAD RULES (data-driven from RECENT PERFORMANCE):
When RECENT PERFORMANCE shows progressionStatus 'plateaued': prescribe a specific change — rep range shift (e.g. move from 3x8 to 4x6), technique variation, or 2.5% load increase.
When progressionStatus 'progressing': acknowledge specifically by exercise name and continue the progression vector. Do not change what is working.
When progressionStatus 'regressing': investigate recovery and volume before prescribing change. Ask about sleep and stress before adjusting load.
When estimatedE1RM is available: use it to prescribe working weights as percentages (e.g. "try 80% of your estimated 1RM for the next 4 weeks").
${ctx.age != null && ctx.age < 25 ? '- Young athlete: emphasize skill development and movement quality over max strength' : ''}
${ctx.age != null && ctx.age > 40 ? '- Experienced athlete: emphasize recovery, joint health, and sustainable progression' : ''}
${ctx.gender === 'Female' ? '- Female athlete: account for hormonal cycles, emphasize relative strength and body composition' : ''}

VOICE & TONE:
Direct, warm, knowledgeable. No generic advice.
No emojis. No bullet points unless asked.
2-4 sentences max unless detailed plan requested.
Sound like a smart friend who knows fitness deeply.

CONVERSATION STYLE:
- You have memory of this conversation — reference what was said earlier when relevant
- If the user mentions something you can cross-reference with their data, do it explicitly
- e.g. "That tracks with your symmetry score of 68 — your left side is lagging"
- Ask one follow-up question at the end of responses when it would deepen the coaching
- If they ask something vague, ask one clarifying question before answering

MEMORY RULES:
- Review the conversation history before responding
- Never repeat specific advice, exercises, or recommendations you have already given in this conversation
- If asked something you have already answered, acknowledge it and build on or deepen the previous answer instead of repeating it
- Keep a mental note of: exercises recommended, rep ranges given, nutrition advice given, and skill progressions suggested

RESPONSE FORMAT:
- 2-4 sentences for simple questions
- For plans or lists: use simple numbered format, no more than 5 items, each item one sentence
- Never use headers, bold, or markdown symbols
- Write the way a smart, direct friend texts — confident, warm, no fluff
- End responses with either a question OR an action — never just a statement that trails off`;
}

/** Dashboard banner — second line only if first line is separate greeting label */
export function getWelcomeMessage(ctx: UserContext): string {
  const hour = new Date().getHours();
  const time = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  if (ctx.latestScore == null)
    return `Good ${time}. Upload your first photo to get your baseline score.`;
  if (ctx.isOvertraining)
    return `Good ${time}. Your last few sessions felt tough — today might be a good day to recover.`;
  if (ctx.isUnderchallenged)
    return `Good ${time}. You've been crushing sessions. Time to push harder.`;
  if (ctx.progressTrend > 5)
    return `Good ${time}. You're up ${ctx.progressTrend} points since you started. Keep going.`;
  if (ctx.progressTrend < -3)
    return `Good ${time}. Scores dipped recently. Let's figure out why.`;
  if (ctx.pointsToGoal != null && ctx.pointsToGoal < 10 && ctx.pointsToGoal > 0)
    return `Good ${time}. You're ${ctx.pointsToGoal} points from your goal. Almost there.`;

  const dayMessages: Record<number, string> = {
    1: `Good ${time}. New week. Make it count.`,
    5: `Good ${time}. Friday. Finish the week strong.`,
    6: `Good ${time}. Weekend — use it.`,
    0: `Good ${time}. Sunday. Rest or train — your call.`,
  };
  return dayMessages[new Date().getDay()] ?? `Good ${time}. Stay consistent.`;
}
